import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Package, Building, AlertTriangle, Users, ShoppingCart, 
  Lightbulb, Clock, User, MessageSquare, CheckCircle, XCircle, 
  Loader2, ChevronRight, Send, Eye, RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REPORT_ICONS = {
  product_issue: Package,
  store_condition: Building,
  incident: AlertTriangle,
  staff_issue: Users,
  customer_demand: ShoppingCart,
  improvement: Lightbulb,
};

const STATUS_STYLES = {
  submitted: { label: 'Submitted', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  acknowledged: { label: 'Acknowledged', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  in_progress: { label: 'In Progress', color: 'bg-orange-100 text-orange-700', icon: Loader2 },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: XCircle },
};

const PRIORITY_STYLES = {
  high: 'bg-red-500 text-white',
  medium: 'bg-yellow-500 text-white',
  low: 'bg-green-500 text-white',
};

export default function ManagerReports() {
  const navigate = useNavigate();
  const { token, user, loading: authLoading } = useAuth();
  
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [updating, setUpdating] = useState(false);

  // Fetch reports
  const fetchReports = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const endpoint = activeTab === 'assigned' ? '/api/staff-reports/assigned' : '/api/staff-reports/all';
      let url = `${API_URL}${endpoint}`;
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('report_type', typeFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_URL}/api/staff-reports/stats/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Load data when token is available
  useEffect(() => {
    if (token && !authLoading) {
      fetchReports();
      fetchStats();
    }
  }, [token, authLoading, activeTab, statusFilter, typeFilter]);

  const updateReportStatus = async (reportId, newStatus) => {
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/staff-reports/${reportId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (res.ok) {
        toast.success(`Report ${newStatus.replace('_', ' ')}`);
        fetchReports();
        fetchStats();
        const data = await res.json();
        setSelectedReport(data.report);
      } else {
        toast.error('Failed to update report');
      }
    } catch (err) {
      toast.error('Error updating report');
    } finally {
      setUpdating(false);
    }
  };

  const submitFeedback = async (reportId) => {
    if (!feedback.trim()) {
      toast.error('Please enter feedback');
      return;
    }
    
    setUpdating(true);
    try {
      const res = await fetch(`${API_URL}/api/staff-reports/${reportId}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ feedback: feedback.trim() })
      });
      
      if (res.ok) {
        toast.success('Feedback sent to staff');
        setFeedback('');
        const data = await res.json();
        setSelectedReport(data.report);
        fetchReports();
      } else {
        toast.error('Failed to send feedback');
      }
    } catch (err) {
      toast.error('Error sending feedback');
    } finally {
      setUpdating(false);
    }
  };

  const pendingCount = reports.filter(r => r.status === 'submitted').length;

  // Show loading while auth initializes
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  // Redirect if not authenticated
  if (!token) {
    return null;
  }

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6" data-testid="manager-reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">Staff Reports</h1>
          <p className="text-sm text-slate-500">Review and respond to staff reports</p>
        </div>
        <Button variant="outline" onClick={() => { fetchReports(); fetchStats(); }}>
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="text-yellow-600" size={24} />
                <div>
                  <p className="text-2xl font-bold text-yellow-700">{stats.status_counts?.submitted || 0}</p>
                  <p className="text-xs text-yellow-600">New Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Loader2 className="text-blue-600" size={24} />
                <div>
                  <p className="text-2xl font-bold text-blue-700">{stats.pending || 0}</p>
                  <p className="text-xs text-blue-600">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <CheckCircle className="text-green-600" size={24} />
                <div>
                  <p className="text-2xl font-bold text-green-700">{stats.status_counts?.resolved || 0}</p>
                  <p className="text-xs text-green-600">Resolved</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Package className="text-slate-600" size={24} />
                <div>
                  <p className="text-2xl font-bold text-slate-700">{stats.total || 0}</p>
                  <p className="text-xs text-slate-600">Total Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Reports List */}
        <div className="lg:col-span-2 space-y-4">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <TabsList>
                <TabsTrigger value="assigned">
                  Assigned to Me
                  {pendingCount > 0 && (
                    <Badge className="ml-2 bg-red-500 text-white text-xs">{pendingCount}</Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="all">All Reports</TabsTrigger>
              </TabsList>
              
              <div className="flex gap-2">
                <select
                  className="px-3 py-1.5 border rounded-lg text-sm"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="submitted">Submitted</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="in_progress">In Progress</option>
                  <option value="resolved">Resolved</option>
                  <option value="rejected">Rejected</option>
                </select>
                <select
                  className="px-3 py-1.5 border rounded-lg text-sm"
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="incident">Incident</option>
                  <option value="staff_issue">Staff Issue</option>
                  <option value="improvement">Improvement</option>
                </select>
              </div>
            </div>

            <TabsContent value="assigned" className="mt-4">
              <ReportsList 
                reports={reports} 
                loading={loading}
                selectedReport={selectedReport}
                onSelect={setSelectedReport}
              />
            </TabsContent>

            <TabsContent value="all" className="mt-4">
              <ReportsList 
                reports={reports} 
                loading={loading}
                selectedReport={selectedReport}
                onSelect={setSelectedReport}
              />
            </TabsContent>
          </Tabs>
        </div>

        {/* Detail Panel */}
        <div className="lg:col-span-1">
          {selectedReport ? (
            <Card className="sticky top-4">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">Report Details</CardTitle>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedReport(null)}
                  >
                    <XCircle size={16} />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Report Info */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    {React.createElement(REPORT_ICONS[selectedReport.report_type] || Package, { size: 20, className: "text-slate-500" })}
                    <span className="font-medium">{selectedReport.report_type_label}</span>
                    <Badge className={PRIORITY_STYLES[selectedReport.priority]}>
                      {selectedReport.priority}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-slate-600">{selectedReport.description}</p>
                  
                  {selectedReport.photo_url && (
                    <img 
                      src={selectedReport.photo_url} 
                      alt="Report" 
                      className="w-full rounded-lg"
                    />
                  )}
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-400">From:</span>
                      <p className="font-medium">{selectedReport.reporter_name}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Store:</span>
                      <p className="font-medium">{selectedReport.store}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Date:</span>
                      <p className="font-medium">{new Date(selectedReport.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <span className="text-slate-400">Status:</span>
                      <Badge className={STATUS_STYLES[selectedReport.status]?.color}>
                        {STATUS_STYLES[selectedReport.status]?.label}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">Quick Actions</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedReport.status === 'submitted' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-blue-600 border-blue-200"
                        onClick={() => updateReportStatus(selectedReport.id, 'acknowledged')}
                        disabled={updating}
                      >
                        <CheckCircle size={14} className="mr-1" />
                        Acknowledge
                      </Button>
                    )}
                    {(selectedReport.status === 'submitted' || selectedReport.status === 'acknowledged') && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="text-orange-600 border-orange-200"
                        onClick={() => updateReportStatus(selectedReport.id, 'in_progress')}
                        disabled={updating}
                      >
                        <Loader2 size={14} className="mr-1" />
                        In Progress
                      </Button>
                    )}
                    {selectedReport.status !== 'resolved' && selectedReport.status !== 'rejected' && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700"
                          onClick={() => updateReportStatus(selectedReport.id, 'resolved')}
                          disabled={updating}
                        >
                          <CheckCircle size={14} className="mr-1" />
                          Resolve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="destructive"
                          onClick={() => updateReportStatus(selectedReport.id, 'rejected')}
                          disabled={updating}
                        >
                          <XCircle size={14} className="mr-1" />
                          Reject
                        </Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Existing Feedback */}
                {selectedReport.feedback && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2">Your Feedback</p>
                    <div className="p-3 bg-blue-50 rounded-lg text-sm">
                      <p className="text-blue-800">{selectedReport.feedback}</p>
                      <p className="text-xs text-blue-600 mt-1">
                        by {selectedReport.feedback_by} • {new Date(selectedReport.feedback_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                )}

                {/* Add Feedback */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium mb-2">
                    {selectedReport.feedback ? 'Update Feedback' : 'Add Feedback'}
                  </p>
                  <textarea
                    className="w-full border rounded-lg px-3 py-2 text-sm resize-none"
                    rows={3}
                    placeholder="Enter feedback for the staff member..."
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                  />
                  <Button 
                    size="sm" 
                    className="w-full mt-2 bg-red-600 hover:bg-red-700"
                    onClick={() => submitFeedback(selectedReport.id)}
                    disabled={updating || !feedback.trim()}
                  >
                    <Send size={14} className="mr-1" />
                    Send Feedback
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-slate-400">
                <Eye size={48} className="mx-auto mb-4 opacity-50" />
                <p>Select a report to view details</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportsList({ reports, loading, selectedReport, onSelect }) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  if (reports.length === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-slate-400">
          <CheckCircle size={48} className="mx-auto mb-4 opacity-50" />
          <p>No reports found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {reports.map((report) => {
        const Icon = REPORT_ICONS[report.report_type] || Package;
        const statusStyle = STATUS_STYLES[report.status] || STATUS_STYLES.submitted;
        const isSelected = selectedReport?.id === report.id;

        return (
          <Card 
            key={report.id}
            className={`cursor-pointer transition-all hover:shadow-md ${
              isSelected ? 'ring-2 ring-red-500 shadow-md' : ''
            }`}
            onClick={() => onSelect(report)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className={`p-2 rounded-lg ${
                  report.status === 'submitted' ? 'bg-yellow-100' : 'bg-slate-100'
                }`}>
                  <Icon size={20} className={
                    report.status === 'submitted' ? 'text-yellow-600' : 'text-slate-600'
                  } />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium text-sm">{report.report_type_label}</span>
                    <Badge className={`${PRIORITY_STYLES[report.priority]} text-xs`}>
                      {report.priority}
                    </Badge>
                    <Badge className={`${statusStyle.color} text-xs`}>
                      {statusStyle.label}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 truncate">{report.description}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <User size={12} />
                      {report.reporter_name}
                    </span>
                    <span className="flex items-center gap-1">
                      <Building size={12} />
                      {report.store}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock size={12} />
                      {new Date(report.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                <ChevronRight size={16} className="text-slate-300 flex-shrink-0" />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
